// Welle 75: Verwaltungsseite für das dauerhafte Gemini-Regelwerk.
// Regeln gelten bei JEDER Gemini-Analyse (Dokumente verschieben, Pipeline 2) als
// System-Instruction. Sie entstehen manuell hier oder aus Korrekturen
// („Als dauerhafte Regel merken" im Pipeline-2-Modal).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";

const API = `${API_BASE}/zuordnen/api/verschieben/ki-regeln`;

const KATEGORIEN = [
  { value: "praefix", label: "Präfix / Empfänger" },
  { value: "absender", label: "Absender" },
  { value: "nummer", label: "Nummern" },
  { value: "datum", label: "Datum" },
  { value: "schema", label: "Dokumenttyp / Schema" },
  { value: "allgemein", label: "Allgemein" },
];
const katLabel = (v) => (KATEGORIEN.find((k) => k.value === v) || {}).label || v;

const S = {
  page: { maxWidth: 1000, margin: "0 auto", padding: "24px 20px 60px", color: "var(--color-gray-800)" },
  h1: { fontSize: "1.5rem", margin: "0 0 4px" },
  sub: { color: "var(--color-gray-600)", margin: "0 0 16px", fontSize: "0.9rem" },
  card: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, padding: 14, marginBottom: 16 },
  inp: { width: "100%", boxSizing: "border-box", padding: "6px 8px", border: "1px solid var(--color-gray-300)", borderRadius: 6, background: "var(--bg-card)", color: "inherit" },
  btn: { padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-gray-300)", background: "var(--bg-card)", cursor: "pointer", fontSize: "0.85rem", color: "inherit" },
  btnPrimary: { padding: "6px 12px", borderRadius: 6, border: "none", background: "var(--color-success, #16a34a)", color: "#fff", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 },
  row: { display: "grid", gridTemplateColumns: "1fr 160px 70px 60px auto", gap: 8, alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--color-gray-200)" },
  meta: { fontSize: "0.72rem", color: "var(--color-gray-500)" },
};

export default function GeminiRegelnPage() {
  const navigate = useNavigate();
  const [regeln, setRegeln] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState("");
  const [neu, setNeu] = useState({ regel: "", kategorie: "praefix", prioritaet: 0 });
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true); setFehler("");
    try {
      const r = await fetch(API);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setRegeln(d.regeln || []);
    } catch (e) { setFehler(String(e.message || e)); }
    finally { setLaedt(false); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const req = async (url, method, body) => {
    const r = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
    return d;
  };

  const anlegen = async () => {
    if (!neu.regel.trim()) return;
    setBusy(true); setFehler("");
    try { await req(API, "POST", neu); setNeu({ regel: "", kategorie: neu.kategorie, prioritaet: 0 }); await laden(); }
    catch (e) { setFehler(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const aendern = async (id, patch) => {
    setBusy(true); setFehler("");
    try { await req(`${API}/${id}`, "PUT", patch); await laden(); }
    catch (e) { setFehler(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const loeschen = async (id) => {
    if (!window.confirm("Regel wirklich löschen?")) return;
    setBusy(true); setFehler("");
    try { await req(`${API}/${id}`, "DELETE"); await laden(); }
    catch (e) { setFehler(String(e.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.page}>
      <button style={{ ...S.btn, marginBottom: 12 }} onClick={() => navigate(-1)}>← Zurück</button>
      <h1 style={S.h1}>🧠 Gemini-Regelwerk</h1>
      <p style={S.sub}>
        Diese Regeln bekommt Gemini bei <b>jeder</b> Dokument-Analyse (Pipeline 2) als dauerhafte Anweisung.
        Sie entstehen hier manuell oder über „Als dauerhafte Regel merken" bei einer Korrektur.
        Höhere Priorität = weiter oben. Inaktive Regeln werden ignoriert, bleiben aber erhalten.
      </p>

      {fehler && <div style={{ ...S.card, borderColor: "var(--color-error)", color: "var(--color-error)" }}>{fehler}</div>}

      <div style={S.card}>
        <strong>Neue Regel</strong>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 180px 80px auto", gap: 8, marginTop: 8, alignItems: "center" }}>
          <input style={S.inp} placeholder="z.B. 'TCH Buchholz' ist ein Objekt → Präfix TCH, keine Person"
            value={neu.regel} onChange={(e) => setNeu({ ...neu, regel: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") anlegen(); }} disabled={busy} />
          <select style={S.inp} value={neu.kategorie} onChange={(e) => setNeu({ ...neu, kategorie: e.target.value })} disabled={busy}>
            {KATEGORIEN.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <input style={S.inp} type="number" title="Priorität" value={neu.prioritaet}
            onChange={(e) => setNeu({ ...neu, prioritaet: Number(e.target.value) || 0 })} disabled={busy} />
          <button style={S.btnPrimary} onClick={anlegen} disabled={busy || !neu.regel.trim()}>+ Anlegen</button>
        </div>
      </div>

      <div style={S.card}>
        <strong>Regeln ({regeln.length})</strong>
        {laedt ? <p style={S.meta}>Wird geladen …</p> : regeln.length === 0 ? (
          <p style={S.meta}>Noch keine Regeln. Gemini arbeitet dann nur mit den allgemeinen Prompt-Regeln.</p>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div style={{ ...S.row, borderTop: "none", ...S.meta }}>
              <span>Regel</span><span>Kategorie</span><span>Prio</span><span>Aktiv</span><span />
            </div>
            {regeln.map((r) => (
              <div key={r.id} style={{ ...S.row, opacity: r.aktiv ? 1 : 0.55 }}>
                <div>
                  <input style={S.inp} defaultValue={r.regel} disabled={busy}
                    onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.regel) aendern(r.id, { regel: v }); }} />
                  <div style={S.meta}>
                    {r.quelle === "korrektur" ? "aus Korrektur" : "manuell"}
                    {r.dateiname ? ` · ${r.dateiname}` : ""}
                    {r.erstellt_am ? ` · ${String(r.erstellt_am).slice(0, 16).replace("T", " ")}` : ""}
                  </div>
                </div>
                <select style={S.inp} value={r.kategorie} disabled={busy} onChange={(e) => aendern(r.id, { kategorie: e.target.value })}>
                  {KATEGORIEN.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
                <input style={S.inp} type="number" defaultValue={r.prioritaet} disabled={busy}
                  onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== r.prioritaet) aendern(r.id, { prioritaet: v }); }} />
                <input type="checkbox" checked={!!r.aktiv} disabled={busy} onChange={(e) => aendern(r.id, { aktiv: e.target.checked })}
                  title={r.aktiv ? "Aktiv — klicken zum Deaktivieren" : "Inaktiv — klicken zum Aktivieren"} />
                <button style={{ ...S.btn, color: "var(--color-error)" }} disabled={busy} onClick={() => loeschen(r.id)} title="Löschen">🗑</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <p style={S.meta}>Kategorien: {KATEGORIEN.map((k) => katLabel(k.value)).join(" · ")}</p>
    </div>
  );
}
